import { Users } from "lucide-react";
import { Card } from "@components/ui/card";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { useEffect, useRef } from "react";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface Player {
  playerId: number;
  clubId: number;
  clubName: string;
  gamePlayed: number;
  questionPlayed: number;
  gameWon: number;
  prizeWon: number;
  totalPlayedTime: number;
  lastPlayedOn: string;
}

interface TopPlayersProps {
  data: Player[];
  loading: boolean;
  error: string | null;
  startDate?: Date;
  endDate?: Date;
}

export function TopPlayers({
  data,
  loading,
  error,
  startDate,
  endDate,
}: TopPlayersProps) {
  // Defensive: support both array and object with players array
  const players = Array.isArray(data) ? data : [];
  // Remove all dropdowns/filters, just show top 10 by games played
  const filtered = players
    .sort((a, b) => b.gamePlayed - a.gamePlayed)
    .slice(0, 10);

  // Tailwind colors for bars: alternate text-blue-500 and text-green-500
  const barColors = ["#3b82f6", "#22c55e"];
  const chartData = {
    labels: filtered.map((p) => p.playerId?.toString() ?? ""),
    datasets: [
      {
        label: "Games Played",
        data: filtered.map((p) => p.gamePlayed),
        backgroundColor: filtered.map(
          (_, i) => barColors[i % barColors.length]
        ),
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    layout: { padding: 0 },
    plugins: {
      legend: { display: false },
      title: {
        display: true,
        text: "Top 10 Players by Games Played",
        color: "#1a2233",
        font: { weight: "bold" as const, size: 13 },
        padding: { top: 20, bottom: 20 },
      },
      tooltip: {
        callbacks: {
          title: function (context: any) {
            return `Player ID: ${filtered[context[0].dataIndex]?.playerId}`;
          },
        },
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: "Player ID",
          color: "#1a2233",
          font: { weight: "bold" as const, size: 13 },
          padding: { top: 16, bottom: 8 },
        },
        ticks: {
          color: "#1a2233",
          font: { size: 12, weight: "bold" as const },
          callback: function (value: any, index: number) {
            return filtered[index]?.playerId?.toString() ?? value;
          },
          padding: 10,
        },
        grid: { color: "#eee" },
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Games Played",
          color: "#222",
          font: { weight: "bold" as const, size: 13 },
          padding: { top: 8, bottom: 8 },
        },
        ticks: {
          color: "#bbb",
          font: { weight: "bold" as const, size: 12 },
          padding: 10,
        },
        grid: { color: "#eee" },
      },
    },
    onClick: (event: any, elements: any[]) => {},
    onHover: (event: any, chartElement: any[]) => {
      const target = event?.native?.target || event.target;
      if (chartElement && chartElement.length > 0) {
        target.style.cursor = "pointer";
      } else {
        target.style.cursor = "default";
      }
    },
  };

  // Add CSS for underline effect on x-axis labels
  // This must be global or in a CSS module loaded by this component
  // Example (add to index.css or a relevant CSS file):
  // .chartjs-x-label-hyperlink { text-decoration: underline; }

  // To apply the class, Chart.js does not support HTML in tick labels, so we use color and fontWeight, but underline must be done via CSS targeting SVG text elements.
  // We'll add a useEffect to add underline style to x-axis labels after render.
  const chartRef = useRef<any>(null);
  useEffect(() => {
    const chart = chartRef.current?.chartInstance || chartRef.current;
    if (chart) {
      const xAxis = chart.scales?.x;
      if (xAxis && xAxis.ticks && xAxis.ticks.length) {
        // Find all x-axis tick labels and underline them
        const svg = chart.canvas.parentNode.querySelector("svg");
        if (svg) {
          const texts = svg.querySelectorAll('g[aria-label="x-axis"] text');
          texts.forEach((el: any) => {
            el.style.textDecoration = "underline";
          });
        }
      }
    }
  }, [chartData, chartOptions]);

  return (
    <Card className="hover:shadow-lg transition-shadow duration-200 overflow-hidden p-6 border border-gray-800">
      <h2 className="text-lg font-semibold font-heading mb-4 flex items-center text-superone-charcoal">
        <Users className="mr-2 h-5 w-5 text-pink-500" />
        Top Players
      </h2>
      {loading ? (
        <div className="text-gray-400">Loading...</div>
      ) : error ? (
        <div className="text-red-500">{error}</div>
      ) : (
        <div>
          <Bar
            ref={chartRef}
            data={chartData}
            options={chartOptions}
            height={100}
          />
        </div>
      )}
    </Card>
  );
}
